import React from 'react';
import { motion } from 'framer-motion';
import { SectionLabel } from './ui/UI';
import { RadarEcho } from './animations/RadarEcho';
/**
 * Periscan Operators — six numbered editorial entries.
 *
 * Previously: six icon+headline+body cards in a 3x2 grid with per-operator
 * accent colors, TiltedCard hover effects, and DecryptedText on each name.
 * That read as the canonical AI feature-grid pattern, plus the multi-color
 * palette made it look like a rainbow chip rack.
 *
 * Now: a single typography-forward list. The operators are functional roles,
 * not characters, so they're presented as named functions — like rows in a
 * staff listing or a print product spec.
 */
const operators = [{
  name: 'Red Team Operator',
  body: 'Builds safe attack scenarios and validates realistic paths.'
}, {
  name: 'Blue Team Operator',
  body: 'Checks whether controls detect, block, log, alert, and route correctly.'
}, {
  name: 'Exposure Operator',
  body: 'Finds which exposures are real, reachable, exploitable, blocked, or noise.'
}, {
  name: 'Remediation Operator',
  body: 'Turns validated risk into fix plans, tickets, and re-tests.'
}, {
  name: 'Evidence Operator',
  body: 'Creates proof for customers, auditors, insurers, boards, and executives.'
}, {
  name: 'AI App Security Operator',
  body: 'Validates AI apps, RAG systems, copilots, agents, tools, and guardrails.'
}];
export function PeriscanOperators() {
  return <section id="operators" aria-labelledby="operators-heading" className="relative w-full py-32 border-t border-white/5 overflow-hidden" data-id="element-1112">
      {/* Continuous radar sweep — reinforces the "AI that does the work"
              framing. The operators are the on-duty sweep; the radar is the
              visual cue that something is always validating. */}
      <RadarEcho intensity={0.5} sweepDuration={13} ringDuration={6.5} rings={3} data-id="element-1113" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1114">
        <SectionLabel number="—" label="Periscan Operators · AI that does the work" className="mb-8" data-id="element-1115" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20" data-id="element-1116">
          <div className="lg:col-span-7" data-id="element-1117">
            <motion.h2 id="operators-heading" initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6
          }} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-[-0.02em] leading-[1.05] text-white" data-id="element-1118">
              AI that does
              <br data-id="element-1119" />
              <span className="text-white/40" data-id="element-1120">the validation work.</span>
            </motion.h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4" data-id="element-1121">
            <p className="text-xl text-white font-medium mb-4" data-id="element-1122">
              Not chatbots. Not magic.
            </p>
            <p className="text-base text-white/65 leading-relaxed" data-id="element-1123">
              Six scoped security operators. They decide what to test, connect
              signals, explain paths, recommend fixes, re-test remediation, and
              prepare evidence — inside customer-approved scope, with policy
              controls and audit trails.
            </p>
          </div>
        </div>

        <ol className="border-t border-white/5 mb-16" data-id="element-1124">
          {operators.map((op, i) => <motion.li key={op.name} initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.5,
          delay: i * 0.04
        }} className="border-b border-white/5 py-10 grid grid-cols-12 gap-6 items-baseline" data-id="element-1125">
              <span className="col-span-12 md:col-span-1 font-mono text-sm text-[#1a8fd4]/70 tracking-[0.2em]" data-id="element-1126">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className="col-span-12 md:col-span-4 text-2xl md:text-3xl text-white font-semibold tracking-tight leading-snug" data-id="element-1127">
                {op.name}
              </h3>
              <p className="col-span-12 md:col-span-7 text-base md:text-lg text-white/65 leading-relaxed max-w-2xl" data-id="element-1128">
                {op.body}
              </p>
            </motion.li>)}
        </ol>

        <p className="text-base md:text-lg text-white/55 max-w-2xl" data-id="element-1129">
          The operators do the repetitive work. Your team keeps control.
        </p>
      </div>
    </section>;
}